/**
 * ConnectToMatterPanel - attaching the tool suite to a matter, in one place.
 *
 * The Connected pane could only ever REPORT: research, drafts and insights
 * arrived already linked from the other side (you started the research from
 * the matter, or generated the draft there), and a review or a table begun
 * from its own section had no way back at all. This is the missing direction —
 * pick a resource, connect it - and it speaks the same endpoint the pane
 * reads:
 *
 *   POST   /matters/{uuid}/connected            { resource_type, resource_uuid }
 *   DELETE /matters/{uuid}/connected/{type}/{uuid}
 *
 * Modelled on `ConnectToProjectPanel`, deliberately: linking a thing to a
 * container is one interaction, and a matter should not invent a second one.
 * Both halves of the relationship are on screen - what is connected (with
 * disconnect) and what can still be connected (searchable) - because "connect
 * a review" is really "manage this matter's connected work".
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiFileMagnifyingGlassLight,
  PiFileTextLight,
  PiLightbulbLight,
  PiLinkBreakLight,
  PiLinkLight,
  PiListMagnifyingGlassLight,
  PiTableLight,
} from 'react-icons/pi';
import apis from '../../../utils/apis';
import { useAppDispatch } from '../../../store';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import Button from '../../shared/TailwindComponents/Button';
import SearchInput from '../../shared/TailwindComponents/SearchInput';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import Badge from '../../shared/TailwindComponents/Badge';
import Spinner from '../../shared/TailwindComponents/Spinner';
import { Well } from '../../shared/brand/Field';
import { formatDateTime } from '../../../utils/functions';
import {
  ConnectableType,
  ConnectedWork,
  EMPTY_CONNECTED,
  connectResourceToMatter,
  disconnectResourceFromMatter,
  toConnectedWork,
} from './useConnectedWork';

export interface ConnectToMatterPanelProps {
  matterUuid: string;
  /** Which type the picker opens on - the group the user pressed "Connect" in. */
  initialType?: ConnectableType;
  /** Called after any connect/disconnect so the pane behind refreshes. */
  onChanged?: () => void;
  onClose: () => void;
}

interface TypeConfig {
  type: ConnectableType;
  /** Untranslated - the caller runs it through `t`. */
  label: string;
  icon: React.ReactElement;
  /** Where the candidates come from. */
  endpoint: string;
  /** Which key of `ConnectedWork` already holds this type. */
  connectedKey: keyof Pick<
    ConnectedWork,
    'research_sessions' | 'drafts' | 'insights' | 'reviews' | 'tables'
  >;
}

/**
 * The five connectable types, in the order the pane lists them.
 *
 * Playbooks, templates and data sources are absent on purpose: they are
 * org-level reusable assets with no matter relation - a playbook is applied to
 * a REVIEW, and it is the review that carries the link. The backend's
 * `connectable_types` says the same thing; this list is filtered by it.
 */
const TYPES: TypeConfig[] = [
  {
    type: 'research_session',
    label: 'Research',
    icon: <PiListMagnifyingGlassLight className="h-4 w-4" />,
    endpoint: '/research/chat-groups',
    connectedKey: 'research_sessions',
  },
  {
    type: 'draft',
    label: 'Drafts',
    icon: <PiFileTextLight className="h-4 w-4" />,
    endpoint: '/drafting/drafts',
    connectedKey: 'drafts',
  },
  {
    type: 'insight',
    label: 'Insights',
    icon: <PiLightbulbLight className="h-4 w-4" />,
    endpoint: '/drafting/insights',
    connectedKey: 'insights',
  },
  {
    type: 'review',
    label: 'Reviews',
    icon: <PiFileMagnifyingGlassLight className="h-4 w-4" />,
    endpoint: '/reviews',
    connectedKey: 'reviews',
  },
  {
    type: 'table',
    label: 'Tables',
    icon: <PiTableLight className="h-4 w-4" />,
    endpoint: '/tables',
    connectedKey: 'tables',
  },
];

/** Every list endpoint nests its rows differently; this is the one reader. */
const readList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.chat_groups)) return data.chat_groups;
  if (Array.isArray(data?.sessions)) return data.sessions;
  if (Array.isArray(data?.reviews)) return data.reviews;
  if (Array.isArray(data?.tables)) return data.tables;
  if (Array.isArray(data?.drafts)) return data.drafts;
  if (Array.isArray(data?.insights)) return data.insights;
  return [];
};

const rowTitle = (row: any, fallback: string): string =>
  row?.title || row?.name || row?.table_name || row?.session_name || fallback;

/**
 * The uuid the CONNECT endpoint addresses this resource by.
 *
 * Research is the one that differs: the pane opens a session by its chat
 * group, and the link is written on the chat group - so `chat_group_uuid`
 * wins where the two are not the same row.
 */
const rowUuid = (row: any, type: ConnectableType): string =>
  String(
    (type === 'research_session' ? row?.chat_group_uuid : undefined)
      || row?.uuid
      || row?.id
      || ''
  );

const ConnectToMatterPanel: React.FC<ConnectToMatterPanelProps> = ({
  matterUuid,
  initialType = 'review',
  onChanged,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [work, setWork] = useState<ConnectedWork>(EMPTY_CONNECTED);
  const [selectedType, setSelectedType] = useState<ConnectableType>(initialType);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [loadingConnected, setLoadingConnected] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  const config = TYPES.find((c) => c.type === selectedType) || TYPES[0];

  /** What the server says it accepts, in the order this panel lists them. */
  const offeredTypes = useMemo(
    () => TYPES.filter((c) => work.connectable_types.includes(c.type)),
    [work.connectable_types]
  );

  const loadConnected = useCallback(async () => {
    if (!matterUuid) return;
    try {
      const response = await apis.get(`/matters/${matterUuid}/connected`);
      setWork(toConnectedWork(response?.data));
    } catch {
      setWork(EMPTY_CONNECTED);
    } finally {
      setLoadingConnected(false);
    }
  }, [matterUuid]);

  useEffect(() => { loadConnected(); }, [loadConnected]);

  /* Candidates for the selected type. Aborted on switch, so a slow research
     page cannot land after the user has moved to Tables and replace it. */
  useEffect(() => {
    const controller = new AbortController();
    setLoadingList(true);
    apis
      .get(config.endpoint, { params: { page: 1, page_size: 50 }, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        setCandidates(readList(response?.data));
      })
      .catch(() => { if (!controller.signal.aborted) setCandidates([]); })
      .finally(() => { if (!controller.signal.aborted) setLoadingList(false); });
    return () => controller.abort();
  }, [config.endpoint]);

  const connectedRows = (work[config.connectedKey] as any[]) || [];
  const connectedUuids = useMemo(
    () => new Set(connectedRows.map((row: any) => rowUuid(row, selectedType))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectedRows, selectedType]
  );

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return candidates.filter((row) => {
      const uuid = rowUuid(row, selectedType);
      if (!uuid || connectedUuids.has(uuid)) return false;
      if (!needle) return true;
      return rowTitle(row, '').toLowerCase().includes(needle);
    });
  }, [candidates, connectedUuids, query, selectedType]);

  const announce = (ok: boolean, connect: boolean) =>
    dispatch(showFeedbackModal({
      showModal: true,
      modalType: ok ? 'success' : 'error',
      duration: 3000,
      message: ok
        ? (connect ? t('Connected to this matter') : t('Disconnected from this matter'))
        : (connect ? t('Could not connect this resource') : t('Could not disconnect this resource')),
    }));

  const runLink = async (uuid: string, connect: boolean) => {
    setBusyUuid(uuid);
    try {
      const ok = connect
        ? await connectResourceToMatter(matterUuid, selectedType, uuid)
        : await disconnectResourceFromMatter(matterUuid, selectedType, uuid);
      announce(ok, connect);
      if (ok) {
        await loadConnected();
        onChanged?.();
      }
    } finally {
      setBusyUuid(null);
    }
  };

  const when = (row: any) => {
    const stamp = row?.updated_at || row?.created_at || row?.last_message_at;
    return stamp ? formatDateTime(stamp) : undefined;
  };

  return (
    <div className="flex flex-col h-full">
      {/* ── Type selector ─────────────────────────────────────────── */}
      <div className="px-5 py-4 border-b border-hairline">
        <p className="text-[11px] font-medium text-fg-muted mb-3">{t('Resource type')}</p>
        <div className="flex flex-wrap gap-1.5">
          {(offeredTypes.length ? offeredTypes : TYPES).map((entry) => (
            <button
              key={entry.type}
              type="button"
              aria-pressed={selectedType === entry.type}
              onClick={() => { setSelectedType(entry.type); setQuery(''); }}
              className={clsx(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs',
                'transition-colors duration-200 ease-slide',
                selectedType === entry.type
                  ? 'bg-champagne/10 text-fg-strong ring-1 ring-champagne/50 font-medium'
                  : 'bg-surface-sunken text-fg-muted hover:text-fg-body'
              )}
            >
              {entry.icon}
              {t(entry.label)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-5 py-4 space-y-6">
        {/* ── Already connected ───────────────────────────────────── */}
        <section>
          <h4 className="text-[11px] font-medium text-fg-muted mb-2">
            {t('Connected')} {connectedRows.length > 0 && `(${connectedRows.length})`}
          </h4>
          {loadingConnected ? (
            <Skeleton variant="list" rows={2} />
          ) : connectedRows.length === 0 ? (
            <p className="text-sm text-fg-muted">
              {t('Nothing of this type is connected to the matter yet.')}
            </p>
          ) : (
            <div className="space-y-2">
              {connectedRows.map((row: any) => {
                const uuid = rowUuid(row, selectedType);
                return (
                  <Well key={uuid} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="text-fg-muted flex-shrink-0">{config.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {rowTitle(row, t('Untitled'))}
                      </p>
                      {when(row) && (
                        <p className="text-[11px] text-fg-muted mt-0.5">{when(row)}</p>
                      )}
                    </div>
                    {row.status && (
                      <Badge color="gray" className="capitalize flex-shrink-0">
                        {t(String(row.status).replace(/_/g, ' '))}
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="small"
                      disabled={busyUuid === uuid}
                      onClick={() => runLink(uuid, false)}
                      icon={<PiLinkBreakLight className="h-4 w-4" />}
                    >
                      {t('Disconnect')}
                    </Button>
                  </Well>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Available to connect ────────────────────────────────── */}
        <section>
          <h4 className="text-[11px] font-medium text-fg-muted mb-2">
            {t('Connect {{type}}', { type: t(config.label).toLowerCase() })}
          </h4>

          <SearchInput
            wrapperClassName="w-full mb-3"
            size="small"
            value={query}
            onChange={setQuery}
            placeholder={t('Search')}
          />

          {loadingList ? (
            <Skeleton variant="list" rows={5} />
          ) : results.length === 0 ? (
            <EmptyState
              icon={config.icon}
              headline={query ? t('Nothing matches your search') : t('Nothing available to connect')}
              subtext={
                query
                  ? t('Try a different search term.')
                  : t('Everything you can connect of this type is already connected.')
              }
            />
          ) : (
            <div className="space-y-2">
              {results.map((row: any) => {
                const uuid = rowUuid(row, selectedType);
                return (
                  <div
                    key={uuid}
                    className="flex items-center gap-3 rounded-md border border-hairline-soft
                               bg-surface-sunken/70 px-3 py-2.5
                               transition-colors duration-200 ease-slide hover:border-champagne/40"
                  >
                    <span className="text-fg-muted flex-shrink-0">{config.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {rowTitle(row, t('Untitled'))}
                      </p>
                      <p className="text-[11px] text-fg-muted mt-0.5 truncate">
                        {[row.status ? t(String(row.status).replace(/_/g, ' ')) : null, when(row)]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="small"
                      disabled={busyUuid === uuid}
                      onClick={() => runLink(uuid, true)}
                      icon={busyUuid === uuid
                        ? <Spinner size="glyph" className="!h-3.5 !w-3.5" />
                        : <PiLinkLight className="h-4 w-4" />}
                    >
                      {t('Connect')}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="flex-shrink-0 border-t border-hairline-soft px-5 py-4 flex justify-end">
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Close')}
        </Button>
      </div>
    </div>
  );
};

export default ConnectToMatterPanel;
